import { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid, InputBase, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Form, Formik } from 'formik';
import { PricingSalesConditions } from 'web/src/pages/AddTerminalPricingPage/PricingSalesConditions';

import { navigate } from '@redwoodjs/router';
import { MetaTags } from '@redwoodjs/web';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { SelectBaseField } from 'src/components/common/form/FormSelect';
import MainCard from 'src/components/MainCard/MainCard';
import { DEFAULT_BORDER } from 'src/constants';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';
import {
    contractTermsMenuItems,
    PricingType,
    SalesConditionsType,
    terminalPricingOfferValidationSchema,
} from 'src/hooks/usePricings';
import { usePricingsOffers } from 'src/hooks/usePricings/usePricingsOffers';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import PricingAccordion from './PricingAccordion';
import { TerminalPricingOfferPartnersModal } from './TerminalPricingPartners';

const AddTerminalPricingPage = () => {
    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Product ID', minWidth: 200 },
        {
            field: 'imageUrl',
            headerName: 'Image',
            minWidth: 200,
            sortable: false,
            renderCell: (params) => {
                const { validateAwsURL } = useGeneratePresignedUrl(); // Hook is defined inside here due to infinate rendering when taken outside
                const [imageUrl, setImageUrl] = useState('');

                useEffect(() => {
                    const fetchPresignedUrl = async () => {
                        const url = params.row.imageUrl;
                        const presignedUrl = await validateAwsURL(url);
                        setImageUrl(presignedUrl ?? url);
                    };
                    fetchPresignedUrl();
                }, [params.row.imageUrl]);

                return (
                    <Box sx={{ padding: '10px' }}>
                        <img src={imageUrl} width="70" alt={`product_${params.row.id}`} />
                    </Box>
                );
            },
        },
        { field: 'name', headerName: 'Product Name', minWidth: 250 },
        {
            field: 'term',
            headerName: 'Contract Term',
            minWidth: 150,
            sortable: false,
        },
        {
            field: 'connectivity',
            headerName: 'Connectivity',
            minWidth: 200,
            sortable: false,
        },
        {
            field: 'action',
            headerName: '',
            minWidth: 300,
            sortable: false,
            renderCell: () => {
                return (
                    <Box className="btn-medium-with-icon btn-dark">
                        <Box className="flex" sx={{ padding: '2px 4px' }}>
                            <AddIcon sx={{ marginRight: '3px' }} />
                            <Box>
                                <Box sx={{ marginTop: '1px', fontSize: '16px' }}>Add</Box>
                            </Box>
                        </Box>
                    </Box>
                );
            },
        },
    ];

    const {
        paginationModel,
        setPrimaryProduct,
        primaryProductList,
        selectedPrimaryProductIds,
        secondaryProductList,
        selectedSecondaryProductIds,
        selectedProducts,
        selectedPartners,
        setSelectedPartners,
        errorMessage,
        open,
        expanded,
        showCustomTerm,
        initialValues,
        onSubmit,
        loadingCreatePricingOffer,
        handlePricingTypeOnChange,
        enableProductForPromo,
        handleSetSelectContractTerm,
        handleFindPrimaryProducts,
        handleSelectedPrimaryProductsIds,
        handleSelectedSecondaryProductsIds,
        handleAccordionChange,
        handleMultiplePricingOnChange,
        handleCloseModal,
        rowCountState,
        handlePaginationModelChange,
        handlePricingLimitChange,
    } = usePricingsOffers(null, PricingType.TERMINAL, false);

    const handleSearchPrimary = () => {
        handleFindPrimaryProducts({ page: paginationModel });
    };

    return (
        <PageContent heading="Add Terminal Pricing offers">
            <MetaTags title="Terminal Pricing Offer" description="Create Terminal Pricing Offer" />
            <Formik
                initialValues={initialValues}
                onSubmit={(_values) => {}}
                validationSchema={terminalPricingOfferValidationSchema}
                enableReinitialize
            >
                {(formikProps) => {
                    const { values, setFieldValue, dirty, isValid, errors } = formikProps;
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <MainCard>
                                    <h2 className="font-normal mb-0 mt-10">
                                        Pricing Offer Information
                                    </h2>
                                    <Box className="mt-20">
                                        <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <InputBaseField
                                                            label="Pricing Offer Name"
                                                            placeholder="Enter Pricing offer name"
                                                            name="name"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <SelectBaseField
                                                            label="Contract Term"
                                                            name="term"
                                                            inputLabel="Select Contract Term"
                                                            menuItems={contractTermsMenuItems}
                                                            setSelectedValue={
                                                                handleSetSelectContractTerm
                                                            }
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    {showCustomTerm && (
                                                        <Grid item xs={12} md={6}>
                                                            <InputBaseField
                                                                placeholder="Enter Custom value"
                                                                type="number"
                                                                label="Custom Term"
                                                                name="customTerm"
                                                            />
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <h3 style={{ color: '#F18F34', marginTop: '30px' }}>
                                                    Primary Product
                                                </h3>
                                                <Stack>
                                                    <label className="font-bold mt-5">
                                                        Search Primary Product
                                                    </label>
                                                    <Box className="mt-10">
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={12} md={6}>
                                                                <Grid container>
                                                                    <Grid
                                                                        item
                                                                        xs={9}
                                                                        className="flex-col"
                                                                    >
                                                                        <InputBase
                                                                            autoComplete="off"
                                                                            name="primaryProduct"
                                                                            className={`form-input-group ${
                                                                                errorMessage
                                                                                    ? 'input-error'
                                                                                    : ''
                                                                            }`}
                                                                            onChange={(event) => {
                                                                                return setPrimaryProduct(
                                                                                    event.target
                                                                                        .value
                                                                                );
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid
                                                                        item
                                                                        xs={3}
                                                                        className="flex-col"
                                                                    >
                                                                        <Button
                                                                            variant="outlined"
                                                                            onClick={
                                                                                handleSearchPrimary
                                                                            }
                                                                            className={`btn-input-group btn-primary ${
                                                                                errorMessage
                                                                                    ? 'btn-error'
                                                                                    : ''
                                                                            }`}
                                                                        >
                                                                            Search
                                                                        </Button>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                    {errorMessage && (
                                                        <label className="error">
                                                            {errorMessage}
                                                        </label>
                                                    )}
                                                    {primaryProductList.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                marginTop: '20px',
                                                                border: 'thin solid #F18F34',
                                                                borderRadius: '10px',
                                                            }}
                                                        >
                                                            <DataGrid
                                                                sx={{
                                                                    '.MuiDataGrid-columnHeaders': {
                                                                        backgroundColor: '#F39E4E',
                                                                        color: '#ffffff',
                                                                        borderRadius:
                                                                            '10px 10px 0 0',
                                                                    },
                                                                }}
                                                                autoHeight
                                                                rows={primaryProductList}
                                                                columns={columns}
                                                                rowCount={rowCountState}
                                                                getRowHeight={() => {
                                                                    return 'auto';
                                                                }}
                                                                paginationModel={paginationModel}
                                                                paginationMode="server"
                                                                disableColumnMenu
                                                                disableColumnFilter
                                                                onPaginationModelChange={
                                                                    handlePaginationModelChange
                                                                }
                                                                pageSizeOptions={[5, 10, 25]}
                                                                onRowSelectionModelChange={(
                                                                    ids
                                                                ) => {
                                                                    handleSelectedPrimaryProductsIds(
                                                                        ids
                                                                    );
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Stack>
                                                {secondaryProductList.length > 0 && (
                                                    <Stack>
                                                        <h3
                                                            className="font-bold mt-40 mb-10"
                                                            style={{ color: '#FDC800' }}
                                                        >
                                                            Secondary Product
                                                        </h3>
                                                        <Box
                                                            sx={{
                                                                ...DEFAULT_BORDER,
                                                            }}
                                                        >
                                                            <DataGrid
                                                                autoHeight
                                                                rows={secondaryProductList}
                                                                columns={columns}
                                                                autoPageSize
                                                                pageSizeOptions={[5]}
                                                                getRowHeight={() => {
                                                                    return 'auto';
                                                                }}
                                                                paginationModel={paginationModel}
                                                                disableColumnFilter
                                                                disableColumnMenu
                                                                onRowSelectionModelChange={(
                                                                    ids
                                                                ) => {
                                                                    return handleSelectedSecondaryProductsIds(
                                                                        ids
                                                                    );
                                                                }}
                                                            />
                                                        </Box>
                                                    </Stack>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Box>
                                </MainCard>
                                {selectedProducts.length > 0 && (
                                    <>
                                        <MainCard>
                                            <Box className="plain-bottom-border">
                                                <h2 className="font-normal mb-0 mt-10">Pricing</h2>
                                            </Box>
                                            {selectedPrimaryProductIds.length > 0 && (
                                                <Box sx={{ marginTop: '30px' }}>
                                                    <h3 style={{ color: '#F18F34' }}>
                                                        Primary Product
                                                    </h3>
                                                    <PricingAccordion
                                                        products={selectedProducts.filter(
                                                            (product) => {
                                                                return (
                                                                    product.catalogue === 'PRIMARY'
                                                                );
                                                            }
                                                        )}
                                                        handleChange={handleAccordionChange}
                                                        expanded={expanded}
                                                        handlePricingTypeOnChange={
                                                            handlePricingTypeOnChange
                                                        }
                                                        handleMultiplePricingOnChange={(price) => {
                                                            return handleMultiplePricingOnChange(
                                                                price
                                                            );
                                                        }}
                                                        handleApplyPromo={enableProductForPromo}
                                                        handlePricingLimitChange={
                                                            handlePricingLimitChange
                                                        }
                                                        styling="primary"
                                                    />
                                                </Box>
                                            )}

                                            {selectedSecondaryProductIds.length > 0 && (
                                                <Box sx={{ marginTop: '30px' }}>
                                                    <h3 style={{ color: '#FDC800' }}>
                                                        Secondary Product
                                                    </h3>
                                                    <PricingAccordion
                                                        products={selectedProducts.filter(
                                                            (product) => {
                                                                return (
                                                                    product.catalogue ===
                                                                    'SECONDARY'
                                                                );
                                                            }
                                                        )}
                                                        handleChange={handleAccordionChange}
                                                        expanded={expanded}
                                                        handlePricingTypeOnChange={
                                                            handlePricingTypeOnChange
                                                        }
                                                        handleMultiplePricingOnChange={(price) => {
                                                            return handleMultiplePricingOnChange(
                                                                price
                                                            );
                                                        }}
                                                        handleApplyPromo={enableProductForPromo}
                                                        handlePricingLimitChange={
                                                            handlePricingLimitChange
                                                        }
                                                        styling="secondary"
                                                    />
                                                </Box>
                                            )}
                                        </MainCard>
                                        <PricingSalesConditions
                                            salesConditions={values.salesConditions}
                                            gpvConditions={values.gpvConditions}
                                            mccConditions={values.mccConditions}
                                            updatePricingConditions={(
                                                fieldName: string,
                                                fieldValue: SalesConditionsType
                                            ) => {
                                                return setFieldValue(fieldName, fieldValue);
                                            }}
                                        />
                                    </>
                                )}

                                <TerminalPricingOfferPartnersModal
                                    open={open}
                                    handleCloseModal={handleCloseModal}
                                    selectedPartners={selectedPartners}
                                    setSelectedPartners={setSelectedPartners}
                                />

                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            onClick={() => {
                                                return navigate('/terminal-pricing');
                                            }}
                                            className="reset-btn btn-medium mr-20"
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {loadingCreatePricingOffer ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    backgroundColor: '#F18F34',
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                loading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Adding Terminal Offer
                                            </LoadingButton>
                                        ) : (
                                            <Box className="btn-wrapper">
                                                <Button
                                                    className={`${
                                                        dirty && isValid && errors
                                                            ? 'btn-primary'
                                                            : 'btn-dark'
                                                    } btn-medium`}
                                                    type="button"
                                                    variant="contained"
                                                    size="medium"
                                                    disableElevation
                                                    onClick={async () => {
                                                        return onSubmit(values);
                                                    }}
                                                >
                                                    Add Terminal Offer
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default AddTerminalPricingPage;
